import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import {
  useGetLabourDailyReportQuery,
  useGetLabourPayrollReportQuery,
  useGetLabourRangeReportQuery,
  LabourPayrollReport,
} from "../../../redux/labourTrackerApi";

const NAVY = "#123456";

function fmtMoney(n: number) {
  return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function currentCycle() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  let sm = m;
  let sy = y;
  if (d < 17) {
    sm = m - 1;
    if (sm < 0) {
      sm = 11;
      sy--;
    }
  }
  const start = new Date(sy, sm, 17);
  const end = new Date(sm === 11 ? sy + 1 : sy, sm === 11 ? 0 : sm + 1, 16);
  return {
    start: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(
      2,
      "0",
    )}-17`,
    end: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(
      2,
      "0",
    )}-16`,
  };
}

// ─── CSV download ──────────────────────────────────────────────────────────
function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Daily Report Tab ──────────────────────────────────────────────────────
const DailyReport: React.FC = () => {
  const [date, setDate] = useState(todayStr());
  const { data, isLoading, isFetching } = useGetLabourDailyReportQuery(date, {
    skip: !date,
  });

  const handleDownload = () => {
    if (!data) return;
    const rows: string[][] = [["Block", "Worker", "Role", "Wage ($)"]];
    data.byBlock.forEach((b) => {
      b.rows.forEach((r) =>
        rows.push([b.blockName, r.name, r.role, String(r.wage)]),
      );
      rows.push([
        `${b.blockName} TOTAL`,
        "",
        String(b.rows.length),
        String(b.total),
      ]);
      rows.push([]);
    });
    rows.push([
      "GRAND TOTAL",
      "",
      String(data.grandCount),
      String(data.grandTotal),
    ]);
    downloadCsv(`daily-report-${date}.csv`, rows);
  };

  return (
    <Box>
      <Box display="flex" alignItems="flex-end" gap={2} mb={3} flexWrap="wrap">
        <TextField
          type="date"
          size="small"
          label="Date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        <Button
          variant="outlined"
          startIcon={<Download />}
          disabled={!data?.byBlock.length}
          onClick={handleDownload}
        >
          Download CSV
        </Button>
        {data && (
          <Typography variant="body2" color="text.secondary" ml="auto">
            {data.grandCount} present · daily wages {fmtMoney(data.grandTotal)}
          </Typography>
        )}
      </Box>

      {(isLoading || isFetching) && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading &&
        !isFetching &&
        data?.byBlock.map((b) => (
          <Card key={b.blockId} sx={{ mb: 2 }}>
            <Box
              px={3}
              py={1.5}
              bgcolor="#f5f7fa"
              borderBottom="1px solid #e8eaed"
            >
              <Box display="flex" justifyContent="space-between">
                <Typography fontWeight={700} color={NAVY}>
                  {b.blockName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {b.artisans} art · {b.assistants} ass · {b.supervisors} sup ·{" "}
                  {fmtMoney(b.total)}
                </Typography>
              </Box>
            </Box>
            <CardContent sx={{ p: 0 }}>
              {b.rows.map((r, i) => (
                <Box
                  key={i}
                  display="flex"
                  justifyContent="space-between"
                  px={3}
                  py={1}
                  sx={{
                    borderBottom:
                      i < b.rows.length - 1 ? "1px solid #f5f5f5" : "none",
                  }}
                >
                  <Typography variant="body2">{r.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.role}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={r.wage > 0 ? 600 : 400}
                  >
                    {r.wage > 0 ? fmtMoney(r.wage) : "—"}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        ))}
      {!isLoading && !isFetching && !data?.byBlock.length && date && (
        <Typography color="text.secondary" textAlign="center" mt={4}>
          No attendance recorded for this date.
        </Typography>
      )}
    </Box>
  );
};

// ─── Payroll Report Table ──────────────────────────────────────────────────
const PayrollTable: React.FC<{
  report: LabourPayrollReport;
  filename: string;
}> = ({ report, filename }) => {
  const handleDownload = () => {
    const rows: string[][] = [
      ["Name", "Role", "Default Block", "Days Present", `Rate ($)`, "Pay ($)"],
    ];
    report.rows.forEach((r) =>
      rows.push([
        r.name,
        r.role,
        r.defaultBlock,
        String(r.daysPresent),
        String(r.rate),
        String(r.pay),
      ]),
    );
    rows.push(["TOTAL", "", "", "", "", String(report.totalPay)]);
    downloadCsv(filename, rows);
  };

  const grouped: Record<string, typeof report.rows> = {};
  report.rows.forEach((r) => {
    if (!grouped[r.role]) grouped[r.role] = [];
    grouped[r.role].push(r);
  });
  const roleOrder = ["Supervisor", "Artisan", "Assistant"];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="body2" color="text.secondary">
          Period: {report.from} → {report.to} · Total pay:{" "}
          {fmtMoney(report.totalPay)}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          size="small"
          onClick={handleDownload}
        >
          Download CSV
        </Button>
      </Box>
      {roleOrder.map((role) => {
        const rows = grouped[role];
        if (!rows?.length) return null;
        const roleTotal = rows.reduce((s, r) => s + r.pay, 0);
        return (
          <Card key={role} sx={{ mb: 2 }}>
            <Box
              px={3}
              py={1.5}
              bgcolor="#f5f7fa"
              borderBottom="1px solid #e8eaed"
              display="flex"
              justifyContent="space-between"
            >
              <Typography fontWeight={700} color={NAVY}>
                {role}s
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fmtMoney(roleTotal)}
              </Typography>
            </Box>
            <CardContent sx={{ p: 0 }}>
              {/* Header */}
              <Box
                display="grid"
                gridTemplateColumns="1fr 120px 80px 80px 80px"
                px={3}
                py={1}
                bgcolor="#fafbfc"
                sx={{ borderBottom: "1px solid #e8eaed" }}
              >
                {["Name", "Default Block", "Days", `Rate`, "Pay"].map((h) => (
                  <Typography
                    key={h}
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
              {rows.map((r, i) => (
                <Box
                  key={r.workerId}
                  display="grid"
                  gridTemplateColumns="1fr 120px 80px 80px 80px"
                  px={3}
                  py={1}
                  sx={{
                    borderBottom:
                      i < rows.length - 1 ? "1px solid #f5f5f5" : "none",
                  }}
                >
                  <Typography variant="body2">{r.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.defaultBlock}
                  </Typography>
                  <Typography variant="body2">{r.daysPresent}</Typography>
                  <Typography variant="body2">{fmtMoney(r.rate)}</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={r.pay > 0 ? 600 : 400}
                    color={r.pay > 0 ? "#1b5e20" : "text.secondary"}
                  >
                    {fmtMoney(r.pay)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        );
      })}
      <Divider />
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Typography fontWeight={700}>
          Total Pay: {fmtMoney(report.totalPay)}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Payroll Tab ───────────────────────────────────────────────────────────
const PayrollReport: React.FC = () => {
  const cycle = currentCycle();
  const { data, isLoading } = useGetLabourPayrollReportQuery();
  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  if (!data) return null;
  return (
    <PayrollTable
      report={data}
      filename={`payroll-${cycle.start}-${cycle.end}.csv`}
    />
  );
};

// ─── Range Tab ────────────────────────────────────────────────────────────
const RangeReport: React.FC = () => {
  const cycle = currentCycle();
  const [from, setFrom] = useState(cycle.start);
  const [to, setTo] = useState(cycle.end);
  const [query, setQuery] = useState<{ from: string; to: string } | null>(null);
  const { data, isLoading } = useGetLabourRangeReportQuery(
    query ?? { from: "", to: "" },
    { skip: !query },
  );

  return (
    <Box>
      <Box display="flex" alignItems="flex-end" gap={2} mb={3} flexWrap="wrap">
        <TextField
          type="date"
          size="small"
          label="From"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          type="date"
          size="small"
          label="To"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Button
          variant="contained"
          onClick={() => setQuery({ from, to })}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}
        >
          Generate
        </Button>
      </Box>
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && data && (
        <PayrollTable
          report={data}
          filename={`range-report-${from}-${to}.csv`}
        />
      )}
    </Box>
  );
};

// ─── Main Reports component ────────────────────────────────────────────────
export const LabourReports: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Reports
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Daily attendance · Monthly payroll (17th → 16th) · Custom range
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ "& .MuiTab-root": { textTransform: "none" }, mb: 3 }}
      >
        <Tab label="Daily" />
        <Tab label="Monthly payroll" />
        <Tab label="Custom range" />
      </Tabs>

      {tab === 0 && <DailyReport />}
      {tab === 1 && <PayrollReport />}
      {tab === 2 && <RangeReport />}
    </Box>
  );
};
